import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type {
  MobileApplication,
  UploadApkResponse,
} from "@/common/models/mobile-application.model";

export const fetchMobileApps = (workspaceId?: number | string) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<MobileApplication[]>(
    httpMethods.GET,
    `${API_ENDPOINTS.MOBILE_APPS.apps}${query}`,
  );
};

export const fetchMobileAppById = (
  id: number | string,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<MobileApplication>(
    httpMethods.GET,
    `${API_ENDPOINTS.MOBILE_APPS.appById(id)}${query}`,
  );
};

export const uploadMobileApk = (
  formData: FormData,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<UploadApkResponse>(
    httpMethods.POST,
    `${API_ENDPOINTS.MOBILE_APPS.upload}${query}`,
    formData,
  );
};

interface MobileAppUploadSession {
  uploadKey: string;
  uploadUrl: string;
  expiresInSeconds: number;
  maxSizeBytes: number;
}

export const uploadMobileAppDirect = async (
  file: File,
  appName: string,
  releaseVersion: string,
  workspaceId?: number | string,
) => {
  const sessionResponse = await httpClient<MobileAppUploadSession>(
    httpMethods.POST,
    `${API_ENDPOINTS.MOBILE_APPS.uploadSession}${workspaceId ? `?workspaceId=${workspaceId}` : ""}`,
    { filename: file.name },
  );
  const session = sessionResponse.data;

  const uploadResponse = await fetch(session.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "x-ms-blob-type": "BlockBlob",
    },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Azure upload failed (${uploadResponse.status})`);
  }

  return httpClient<UploadApkResponse>(
    httpMethods.POST,
    `${API_ENDPOINTS.MOBILE_APPS.uploadFinalize}${workspaceId ? `?workspaceId=${workspaceId}` : ""}`,
    {
      uploadKey: session.uploadKey,
      filename: file.name,
      name: appName || undefined,
      releaseVersion,
    },
  );
};

export const deleteMobileApp = (
  id: number | string,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<{ success: boolean }>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.MOBILE_APPS.deleteApp(id)}${query}`,
  );
};

export const deleteMobileAppVersion = (
  appId: number | string,
  versionId: number | string,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<{ success: boolean }>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.MOBILE_APPS.deleteVersion(appId, versionId)}${query}`,
  );
};

export const setMobileAppVersionPinned = (
  appId: number | string,
  versionId: number | string,
  pinned: boolean,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<{ success: boolean; isPinned: boolean }>(
    httpMethods.PATCH,
    `${API_ENDPOINTS.MOBILE_APPS.pinVersion(appId, versionId)}${query}`,
    { pinned },
  );
};

export const setWorkspaceDefaultMobileApp = (
  applicationId: number | null,
  workspaceId?: number | string,
) => {
  return httpClient<{ success: boolean }>(
    httpMethods.PATCH,
    API_ENDPOINTS.MOBILE_APPS.setDefault,
    {
      applicationId,
      workspaceId,
    },
  );
};

export const fetchArtifactDownloadUrl = (
  artifactId: number | string,
  workspaceId?: number | string,
) => {
  const query = workspaceId ? `?workspaceId=${workspaceId}` : "";
  return httpClient<{
    downloadUrl: string;
    fileName: string;
    checksum?: string;
  }>(
    httpMethods.GET,
    `${API_ENDPOINTS.MOBILE_APPS.artifactDownload(artifactId)}${query}`,
  );
};
